/**
 * Corpo da página travado por modal que não devolveu o controle (CHAT002-4515).
 *
 * ═══ O QUE FOI MEDIDO (PostHog, 7 dias até 22/09/2026) ═══
 *
 * 68 de 308 pessoas que passaram pelo /comecar clicaram numa tela que não
 * respondia — 22%. O clique morto cai no próprio <body>, com esta assinatura:
 *
 *     body  style="pointer-events: none;"  data-scroll-locked="2"
 *
 * A mesma assinatura aparece em outras cinco telas (/profile, /escritorio/
 * demandas, /escritorio/clientes, /agents/v2 e a home), então não é defeito de
 * UMA tela: é a classe inteira.
 *
 * ═══ O MECANISMO ═══
 *
 * Ao abrir um modal, o Radix grava o `pointer-events` ORIGINAL do body e põe
 * `none`; ao fechar, devolve o valor gravado. Quando duas camadas modais se
 * sobrepõem — um Dialog que fecha no mesmo instante em que outro abre (os três
 * passos do QualifyForms fazem exatamente isso) — a segunda grava como
 * "original" o `none` que a primeira acabou de pôr. No fim, o body volta para
 * `none` e nada mais é clicável. O `data-scroll-locked` é do react-remove-scroll,
 * que conta as travas: o "2" é a contagem que não voltou a zero.
 *
 * ═══ O QUE ESTA FUNÇÃO FAZ, E POR QUE NÃO É GAMBIARRA ═══
 *
 * Ela só age quando o estado é IMPOSSÍVEL de ser legítimo: body travado e
 * NENHUMA camada aberta na página. Com modal, menu, lista ou popover aberto, a
 * trava é do próprio componente e fica intocada. Sem camada nenhuma, não existe
 * dono para a trava — ela é resto, e o usuário está olhando para uma tela que
 * ignora o clique sem dizer nada.
 *
 * Ela não conserta a sobreposição; conserta o resultado dela, em qualquer tela,
 * inclusive nas que ainda não descobrimos. E o reparo é MEDIDO: quem a chama
 * emite um evento no PostHog, para a falha silenciosa passar a ter número.
 */

/** Camadas que PODEM legitimamente travar o body enquanto estiverem abertas. */
export const SELETOR_CAMADA_ABERTA = [
	'[role="dialog"][data-state="open"]',
	'[role="alertdialog"][data-state="open"]',
	'[role="menu"][data-state="open"]',
	'[role="listbox"][data-state="open"]',
	"[data-radix-popper-content-wrapper]",
].join(",");

export interface ResultadoDoReparo {
	/** `true` se havia trava sem dono e ela foi removida. */
	reparou: boolean;
	/** O que estava travado — vai no evento, para saber qual das duas travas sobra mais. */
	pointerEventsTravado: boolean;
	travasDeScroll: string | null;
}

export function repararCorpoTravado(
	doc: Document = document
): ResultadoDoReparo {
	const body = doc.body;
	const pointerEventsTravado = body.style.pointerEvents === "none";
	const travasDeScroll = body.getAttribute("data-scroll-locked");

	if (!pointerEventsTravado && travasDeScroll === null) {
		return { reparou: false, pointerEventsTravado, travasDeScroll };
	}

	// Há camada aberta: a trava tem dono. Mexer aqui quebraria o modal que o
	// usuário está usando AGORA.
	if (doc.querySelector(SELETOR_CAMADA_ABERTA)) {
		return { reparou: false, pointerEventsTravado, travasDeScroll };
	}

	if (pointerEventsTravado) {
		body.style.pointerEvents = "";
	}
	// O react-remove-scroll trava o scroll por CSS em `body[data-scroll-locked]`.
	// Tirar o atributo é o que devolve a rolagem — e o contador interno dele,
	// que é onde o "2" ficou preso, deixa de importar enquanto não houver trava.
	if (travasDeScroll !== null) {
		body.removeAttribute("data-scroll-locked");
	}

	return { reparou: true, pointerEventsTravado, travasDeScroll };
}
